import { captureException } from '@sentry/react';
import { setSocketUsers } from '../actions/socketActions';
import { setUserList } from '../actions/userActions';
import baseApi, { TAGS } from '../api/baseApi';
import { isArray, isEmpty, map } from 'lodash';

/************************************************** UTILS **************************************************/
const formatExpenses = (expenseList) => {
  const expenses = expenseList.map((data) => {
    let status = data.status;
    if (data.status === 'yes' && data.user_notified === 0) {
      status = 'prob';
    } else if (data.status === 'maybe' && data.user_notified === 1) {
      status = 'maybe*';
    }
    const retObj = {
      ...data,
      amount: data.amount % 1 !== 0 ? data.amount.toFixed(2) : data.amount,
      collapsed: false,
      changed: !!(status === 'prob' || status === 'maybe'),
      status
    };
    return retObj;
  });
  return expenses;
};

// Helper function to build query params
const buildQueryParams = (params) => {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (isEmpty(value)) {
      return;
    }
    if (isArray(value)) {
      value.forEach((v) => searchParams.append(`${key}[]`, v));
    } else {
      searchParams.append(key, value);
    }
  });
  return searchParams;
};

/************************************************** API **************************************************/
export const userApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserInfo: builder.query({
      query: (userId) => ({
        url: `users/${userId}`,
        method: 'GET'
      }),
      transformResponse: (response) => response.data,
      providesTags: [TAGS.USER_INFO]
    }),
    getUserList: builder.query({
      query: (searchText = '') => {
        const params = buildQueryParams({
          searchText
        });
        return {
          url: 'users',
          method: 'GET',
          params
        };
      },
      transformResponse: (response) => response.data,
      providesTags: [TAGS.USER_LIST],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          // This is needed in order to get the socket operations to work
          const { data: users } = await queryFulfilled;
          if (!isEmpty(users)) {
            dispatch(setUserList(users));
            dispatch(setSocketUsers(map(users, 'userId')));
          }
          // Invalidate num blue dots to refresh the count
          dispatch(baseApi.util.invalidateTags([TAGS.NUM_BLUE_DOTS]));
        } catch (e) {
          captureException(e);
        }
      }
    }),
    setHold: builder.mutation({
      query: ({ userId, isOnHold, holdingBookkeeperId }) => ({
        url: `users/${userId}/bookkeeper-assignments/hold`,
        method: 'PUT',
        body: { isOnHold, holdingBookkeeperId }
      }),
      invalidatesTags: [TAGS.USER_BOOKKEEPER_ASSIGNMENTS, TAGS.USER_LIST],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          captureException(e);
        }
      }
    }),
    getUserBookkeeperAssignments: builder.query({
      query: ({ userId }) => ({
        url: `users/${userId}/bookkeeper-assignments`,
        method: 'GET'
      }),
      transformResponse: (response) => response.data,
      providesTags: [TAGS.USER_BOOKKEEPER_ASSIGNMENTS]
    }),
    assignUserToBookkeeper: builder.mutation({
      query: ({ userId }) => ({
        url: `users/${userId}/bookkeeper-assignments`,
        method: 'POST'
      }),
      invalidatesTags: [TAGS.USER_BOOKKEEPER_ASSIGNMENTS, TAGS.USER_LIST]
    }),
    unassignUserFromBookkeeper: builder.mutation({
      query: ({ userId, bookkeeperId }) => ({
        url: `users/${userId}/bookkeeper-assignments/${bookkeeperId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [TAGS.USER_BOOKKEEPER_ASSIGNMENTS, TAGS.USER_LIST]
    }),
    getUnnoticedUsers: builder.query({
      query: () => ({
        url: 'users/unnoticed',
        method: 'GET'
      }),
      transformResponse: (response) => response.data,
      providesTags: [TAGS.UNNOTICED_USERS]
    }),
    createImpersonationLink: builder.mutation({
      query: (impersonateeId) => ({
        url: `users/${impersonateeId}/impersonation-link`,
        method: 'POST'
      }),
      transformResponse: (response) => response.data,
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          window.open(data.link);
        } catch (e) {
          captureException(e);
        }
      }
    }),
    getSignedUrl: builder.query({
      query: ({ userId, key }) => ({
        url: `users/${userId}/files/${key}/signed-url`,
        method: 'GET'
      }),
      transformResponse: (response) => response.data
    }),
    exportUserData: builder.query({
      query: (userId) => ({
        url: `users/${userId}/export-data`,
        method: 'GET',
        timeout: 60_000
      }),
      transformResponse: (response) => response.data
    }),
    importUserData: builder.mutation({
      query: (body) => ({
        url: 'users/import-data',
        method: 'POST',
        body,
        timeout: 60_000
      }),
      transformResponse: (response) => response.data,
      invalidatesTags: [TAGS.USER_LIST]
    }),
    updateUser: builder.mutation({
      query: ({ userId, body }) => ({
        url: `users/${userId}`,
        method: 'PATCH',
        body
      }),
      invalidatesTags: [TAGS.USER_INFO, TAGS.USER_LIST],
      async onQueryStarted({ userId, body }, { dispatch, queryFulfilled }) {
        // Optimistic update for isAskEnabled and autoPredict flags
        const patchResult = dispatch(
          userApi.util.updateQueryData('getUserInfo', userId, (draft) => {
            if (body.isAskEnabled !== undefined) {
              draft.isAskEnabled = body.isAskEnabled;
            }
            if (body.autoPredict !== undefined) {
              draft.autoPredict = body.autoPredict;
            }
          })
        );

        // Optimistic update for user list if the user is in the list
        const userListPatchResult = dispatch(
          userApi.util.updateQueryData('getUserList', {}, (draft) => {
            const userIndex = draft.findIndex((user) => user.userId === userId);
            if (userIndex !== -1) {
              if (body.isAskEnabled !== undefined) {
                draft[userIndex].isAskEnabled = body.isAskEnabled;
              }
              if (body.autoPredict !== undefined) {
                draft[userIndex].autoPredict = body.autoPredict;
              }
            }
          })
        );

        try {
          await queryFulfilled;
        } catch (e) {
          patchResult.undo();
          userListPatchResult.undo();
          captureException(e);
        }
      }
    }),
    getExpenses: builder.query({
      query: (userId) => ({
        url: `users/${userId}/expenses`,
        method: 'GET'
      }),
      transformResponse: (response) => formatExpenses(response.data),
      providesTags: [TAGS.EXPENSES],
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          captureException(e);
        }
      }
    }),
    getDeductionTotals: builder.query({
      query: (userId) => ({
        url: `users/${userId}/expenses/deduction-totals`,
        method: 'GET'
      }),
      transformResponse: (response) => response.data.deductionTotals
    }),
    saveExpenseChange: builder.mutation({
      query: ({ userId, transactionId, ...body }) => ({
        url: `users/${userId}/expenses/${transactionId}`,
        method: 'PATCH',
        body
      }),
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (e) {
          captureException(e);
        }
      }
    })
  })
});

/************************************************** HOOKS **************************************************/
export const {
  useGetUserInfoQuery,
  useGetUserListQuery,
  useSetHoldMutation,
  useGetUserBookkeeperAssignmentsQuery,
  useAssignUserToBookkeeperMutation,
  useUnassignUserFromBookkeeperMutation,
  useGetUnnoticedUsersQuery,
  useCreateImpersonationLinkMutation,
  useLazyGetSignedUrlQuery,
  useUpdateUserMutation,
  useLazyExportUserDataQuery,
  useImportUserDataMutation,
  useGetExpensesQuery,
  useGetDeductionTotalsQuery,
  useSaveExpenseChangeMutation
} = userApi;
